import {utils} from '@wallaby-ui/core';
import {assign, extend, find, forOwn, has, isArray, map} from 'lodash';

import {KEYWORD_PROP_CONTENT_VALUE} from './auto_layout_screen_data_helpers';
import {LIBRARY_NAME} from 'src/js/common/constants';

const logger = utils.LogUtils.getLogger('AutoLayoutLayoutSchema', LIBRARY_NAME);

const KEYWORD_SCHEMA_CORE = 'core';
const KEYWORD_SCHEMA_DEPENDENCIES = 'dependencies';

const KEYWORD_COMP_CONTENT_OBJECT = 'contentObject';
const KEYWORD_COMP_ID = 'id';
export const KEYWORD_COMP_LAYOUT_ITEMS = 'layoutItems';
const KEYWORD_COMP_LAYOUT_TYPE = 'layoutType';
const KEYWORD_COMP_COMPONENT_TYPE = 'componentType';
const KEYWORD_COMP_COLLAPSIBLE = 'collapsible';
const KEYWORD_COMP_COLLAPSED_DEFAULT = 'collapsedDefault';
const KEYWORD_COMP_CUSTOM_CONFIG = 'customConfig';
const KEYWORD_PROP_COLLAPSIBLE = 'collapsible';

const KEYWORD_PROP_CONTENT_PROPERTY = 'contentProperty';
const KEYWORD_PROP_DEFAULT_COMPONENT = 'defaultComponent';
const KEYWORD_PROP_HIDDEN_VALUES = 'hiddenValues';
const KEYWORD_PROP_EXPANDABLE = 'expandable';
const KEYWORD_PROP_EXPANDABLE_DATA = 'expandableData';
const KEYWORD_PROP_EXPAND_TYPE = 'expandType';
export const KEYWORD_PROP_EXPAND_LAYOUT = 'expandLayout';
const KEYWORD_PROP_SEPARATOR = 'separator';
const KEYWORD_PROP_LABEL = 'label';
const KEYWORD_PROP_LAZY_RENDERING = 'lazyRendering';
const KEYWORD_PROP_NAME = 'name';
const KEYWORD_PROP_ORDER = 'order';
const KEYWORD_PROP_SECTION_TYPE = 'sectionType';
const KEYWORD_PROP_READ_ONLY = 'readOnly';
const KEYWORD_PROP_LAYOUT_TYPE = 'layoutType';

const KEYWORD_DEFAULT_COMPONENT_ID = 'id';
const KEYWORD_DEFAULT_COMPONENT_DISPLAY_ALL = 'displayAll';
const KEYWORD_DEFAULT_COMPONENT_DISPLAY_PROPERTY = 'displayProperty';
const KEYWORD_DEFAULT_COMPONENT_MAPPED_BODY_PROPERTY = 'mappedBodyProperty';
export const KEYWORD_DEFAULT_COMPONENT_NUM_LINES = 'numLines';

const KEYWORD_CONTAINER = 'container';
const KEYWORD_CONTAINER_LAYOUT_ITEMS = 'layoutItems';
const KEYWORD_CONTAINER_CONTAINER_TYPE = 'containerType';

export class LayoutContainer {
  constructor(containerLayoutDefinition) {
    assign(this, containerLayoutDefinition);

    this.extendProperties();
  }

  extendProperties() {
    /* eslint-disable no-use-before-define */
    // extend layoutItems
    if (isArray(this[KEYWORD_CONTAINER_LAYOUT_ITEMS])) {
      this[KEYWORD_CONTAINER_LAYOUT_ITEMS] =
        new LayoutComponentLayoutItemsArray(
          this[KEYWORD_CONTAINER_LAYOUT_ITEMS]
        );
    } else {
      this[KEYWORD_CONTAINER_LAYOUT_ITEMS] = new LayoutComponentLayoutItems(
        this[KEYWORD_CONTAINER_LAYOUT_ITEMS]
      );
    }
    /* eslint-enable no-use-before-define */
  }

  getLayoutType() {
    return this[KEYWORD_PROP_LAYOUT_TYPE];
  }

  getContainerType() {
    return this[KEYWORD_CONTAINER_CONTAINER_TYPE];
  }

  getLayoutItems() {
    return this[KEYWORD_CONTAINER_LAYOUT_ITEMS];
  }
}

export class LayoutDefaultComponent {
  constructor(defaultComponentLayoutDefinition) {
    if (typeof defaultComponentLayoutDefinition === 'object') {
      assign(this, defaultComponentLayoutDefinition);
    } else {
      this[KEYWORD_PROP_CONTENT_VALUE] = defaultComponentLayoutDefinition;
    }
  }

  getId() {
    return this.isValueType()
      ? this[KEYWORD_PROP_CONTENT_VALUE]
      : this[KEYWORD_DEFAULT_COMPONENT_ID];
  }

  getDisplayAll() {
    return this[KEYWORD_DEFAULT_COMPONENT_DISPLAY_ALL];
  }

  getDisplayProperty() {
    return this[KEYWORD_DEFAULT_COMPONENT_DISPLAY_PROPERTY];
  }

  getMappedBodyProperty() {
    return this[KEYWORD_DEFAULT_COMPONENT_MAPPED_BODY_PROPERTY];
  }

  isValueType() {
    return has(this, KEYWORD_PROP_CONTENT_VALUE);
  }

  getRawDataObject() {
    let rawData;
    if (this.isValueType()) {
      rawData = this[KEYWORD_PROP_CONTENT_VALUE];
    } else {
      rawData = {};
      assign(rawData, this);
    }
    return rawData;
  }
}

export class LayoutProperty {
  constructor(propertyLayoutDefinition) {
    if (typeof propertyLayoutDefinition === 'object') {
      assign(this, propertyLayoutDefinition);

      this.extendProperties();
    } else {
      this[KEYWORD_PROP_CONTENT_VALUE] = propertyLayoutDefinition;
    }
  }

  extendProperties() {
    // extend defaultComponent
    if (this[KEYWORD_PROP_DEFAULT_COMPONENT]) {
      this[KEYWORD_PROP_DEFAULT_COMPONENT] = new LayoutDefaultComponent(
        this[KEYWORD_PROP_DEFAULT_COMPONENT]
      );
    }
  }

  $valueOf() {
    return this.isValueType() ? this[KEYWORD_PROP_CONTENT_VALUE] : this;
  }

  /**
   *
   * @returns {LayoutProperty.label|string}
   */
  getLabel() {
    return this[KEYWORD_PROP_LABEL];
  }

  /**
   *
   * @returns {LayoutProperty.name|string}
   */
  getName() {
    return this[KEYWORD_PROP_NAME];
  }

  /**
   *
   * @returns {LayoutProperty.contentProperty|string}
   */
  getContentProperty() {
    return this[KEYWORD_PROP_CONTENT_PROPERTY];
  }

  /**
   *
   * @returns {LayoutProperty.contentProperty|string}
   */
  isCollapsible() {
    return this[KEYWORD_PROP_COLLAPSIBLE];
  }

  /**
   *
   * @returns {LayoutProperty.order|number}
   */
  getOrder() {
    return this[KEYWORD_PROP_ORDER];
  }

  /**
   *
   * @returns {LayoutProperty.lazyRendering|boolean}
   */
  isLazyRendering() {
    return this[KEYWORD_PROP_LAZY_RENDERING];
  }

  /**
   *
   * @returns {LayoutProperty.expandableData|boolean}
   */
  hasExpandableData() {
    return this[KEYWORD_PROP_EXPANDABLE_DATA];
  }

  getSeparator() {
    return this[KEYWORD_PROP_SEPARATOR];
  }

  /**
   *
   * @returns {LayoutProperty.expandable|boolean}
   */
  isExpandable() {
    return this[KEYWORD_PROP_EXPANDABLE];
  }

  /**
   *
   * @returns {LayoutProperty.expandType|string}
   */
  getExpandType() {
    return this[KEYWORD_PROP_EXPAND_TYPE];
  }

  /**
   *
   * @returns {LayoutProperty.expandLayout|string}
   */
  getExpandLayout() {
    return this[KEYWORD_PROP_EXPAND_LAYOUT];
  }

  /**
   *
   * @returns {LayoutProperty.defaultComponent}
   */
  getDefaultComponent() {
    return this[KEYWORD_PROP_DEFAULT_COMPONENT];
  }

  getHiddenValues() {
    return this[KEYWORD_PROP_HIDDEN_VALUES];
  }

  getSectionType() {
    return this[KEYWORD_PROP_SECTION_TYPE];
  }

  getReadOnly() {
    return this[KEYWORD_PROP_READ_ONLY] === true;
  }

  isValueType() {
    return has(this, KEYWORD_PROP_CONTENT_VALUE);
  }

  getRawDataObject() {
    let rawData;
    if (this.isValueType()) {
      rawData = this[KEYWORD_PROP_CONTENT_VALUE];
    } else {
      rawData = {};
      assign(rawData, this);

      if (rawData[KEYWORD_PROP_DEFAULT_COMPONENT]) {
        rawData[KEYWORD_PROP_DEFAULT_COMPONENT] =
          rawData[KEYWORD_PROP_DEFAULT_COMPONENT].getRawDataObject();
      }
    }
    return rawData;
  }

  isHandledRecursively() {
    return !this.getDefaultComponent();
  }
}

export class LayoutComponent extends LayoutProperty {
  constructor(componentLayoutDefinition) {
    super(componentLayoutDefinition);
    assign(this, componentLayoutDefinition);

    this.extendProperties();
    this.$flattenProperties = LayoutComponent._createFlattenProperties(
      this.getLayoutItems()
    );
    Object.defineProperty(this, '$flattenProperties', {enumerable: false});

    this.$initialized = true;
  }

  _init(componentLayoutDefinition) {
    if (!this.$initialized) {
      extend(this, componentLayoutDefinition);

      this.extendProperties();
      this.$flattenProperties = LayoutComponent._createFlattenProperties(
        this.getLayoutItems()
      );
      Object.defineProperty(this, '$flattenProperties', {enumerable: false});

      this.$initialized = true;
    }
  }

  /* eslint-disable no-use-before-define */
  extendProperties() {
    // extend layoutItems
    if (isArray(this[KEYWORD_CONTAINER_LAYOUT_ITEMS])) {
      this[KEYWORD_CONTAINER_LAYOUT_ITEMS] =
        new LayoutComponentLayoutItemsArray(
          this[KEYWORD_CONTAINER_LAYOUT_ITEMS]
        );
    } else {
      this[KEYWORD_CONTAINER_LAYOUT_ITEMS] = new LayoutComponentLayoutItems(
        this[KEYWORD_CONTAINER_LAYOUT_ITEMS]
      );
    }
  }

  /* eslint-enable no-use-before-define */

  static _pushChildrenOfLayoutItems(pushFrom, pushTo) {
    let name;
    forOwn(pushFrom, (value, key) => {
      name = value.name || key;
      pushTo.push({
        $flattenChildName: name,
        $flattenChildValue: value,
      });
    });
  }

  static _createFlattenProperties(layoutItems) {
    const unprocessedQueue = [];
    const resultQueue = [];
    let currItem;
    let layoutProperty;
    if (layoutItems) {
      LayoutComponent._pushChildrenOfLayoutItems(layoutItems, unprocessedQueue);
      while (unprocessedQueue.length > 0) {
        currItem = unprocessedQueue.pop();
        layoutProperty = currItem.$flattenChildValue || currItem;
        if (layoutProperty instanceof LayoutContainer) {
          LayoutComponent._pushChildrenOfLayoutItems(
            layoutProperty.getLayoutItems(),
            unprocessedQueue
          );
        } else {
          resultQueue.push(currItem);
        }
      }
    }
    return resultQueue;
  }

  getFlattenProperties() {
    return this.$flattenProperties;
  }

  /**
   *
   * @returns {LayoutComponent.id|string}
   */
  getId() {
    return this[KEYWORD_COMP_ID];
  }

  /**
   *
   * @returns {LayoutComponent.contentObject|string}
   */
  getContentObject() {
    return this[KEYWORD_COMP_CONTENT_OBJECT];
  }

  /**
   *
   * @returns {LayoutComponent.layoutItems|LayoutComponentLayoutItems}
   */
  getLayoutItems() {
    return this[KEYWORD_COMP_LAYOUT_ITEMS];
  }

  static _findProperty(
    layoutItems,
    flattenProperties,
    propertyId,
    byContentProperty = false
  ) {
    let result;
    const flattenPropertyById = find(flattenProperties, (flattenProperty) =>
      byContentProperty
        ? flattenProperty.$flattenChildValue.contentProperty === propertyId
        : flattenProperty.$flattenChildName === propertyId
    );
    if (flattenPropertyById) {
      result = flattenPropertyById.$flattenChildValue;
    } else {
      logger.error(
        `Cannot find property [${propertyId}] in flatten properties.`
      );
    }
    return result;
  }

  /**
   *
   * @param {string} propertyId
   * @returns {LayoutProperty|Object}
   */
  getProperty(propertyId, byContentProperty = false) {
    return LayoutComponent._findProperty(
      this.getLayoutItems(),
      this.getFlattenProperties(),
      propertyId,
      byContentProperty
    );
  }

  /**
   *
   * @returns {LayoutComponent.componentType|string}
   */
  getComponentType() {
    return this[KEYWORD_COMP_COMPONENT_TYPE];
  }

  /**
   *
   * @returns {LayoutComponent.componentType|string}
   */
  isCollapsible() {
    return this[KEYWORD_COMP_COLLAPSIBLE];
  }

  getCollapsedDefault() {
    return this[KEYWORD_COMP_COLLAPSED_DEFAULT];
  }

  /**
   * Gets content property definition by pathToData reference.
   *
   * @param {string} pathToData
   * @param {LayoutSchema} rootLayoutDef
   * @returns {LayoutProperty} content property definition
   */
  getPropertyByPathToData(pathToData, rootLayoutDef) {
    const dotIndex = pathToData.indexOf('.');
    const openBracketIndex = pathToData.indexOf('[');
    let result;
    if (dotIndex < openBracketIndex) {
      const componentProperty = pathToData.substring(0, dotIndex);
      const componentPathToData = pathToData.substring(dotIndex + 1);
      const component = rootLayoutDef.getComponent(
        this.getProperty(componentProperty, true).getExpandLayout()
      );
      result = component.getPropertyByPathToData(componentPathToData);
    } else if (openBracketIndex < dotIndex) {
      const componentProperty = pathToData.substring(0, openBracketIndex);
      const componentPathToData = pathToData.substring(dotIndex + 1);
      const component = rootLayoutDef.getComponent(
        this.getProperty(componentProperty, true).getExpandLayout()
      );
      result = component.getPropertyByPathToData(componentPathToData);
    } else {
      // dotIndex === -1 && openBracketIndex === -1
      result = this.getProperty(pathToData, true);
    }
    return result;
  }
}

export class LayoutComponentLayoutItemsArray extends Array {
  /* eslint-disable no-param-reassign */
  static extendProperties(arr) {
    forOwn(arr, (value, key) => {
      if (
        arr[key][KEYWORD_PROP_LAYOUT_TYPE] &&
        arr[key][KEYWORD_PROP_LAYOUT_TYPE] === KEYWORD_CONTAINER
      ) {
        arr[key] = new LayoutContainer(value);
      } else {
        arr[key] = new LayoutProperty(value);
      }
    });
  }

  constructor(layoutItemsDefinition) {
    if (isArray(layoutItemsDefinition)) {
      // call Array constructor with items
      super(...layoutItemsDefinition);

      Object.setPrototypeOf(this, LayoutComponentLayoutItemsArray.prototype);

      LayoutComponentLayoutItemsArray.extendProperties(this);
      this.$flattenProperties = LayoutComponent._createFlattenProperties(this);
      Object.defineProperty(this, '$flattenProperties', {enumerable: false});
    } else {
      // call Array constructor with size
      super(layoutItemsDefinition);
      Object.setPrototypeOf(this, LayoutComponentLayoutItemsArray.prototype);
    }
  }

  /**
   * Determine if the given object is an array.
   * @param value
   * @returns {boolean}
   */
  // eslint-disable-next-line class-methods-use-this
  isArray(value) {
    // get it's stringified version
    const stringValue = Object.prototype.toString.call(value);

    // check to see if the string representation denotes array
    return stringValue.toLowerCase() === '[object array]';
  }

  /* eslint-enable no-param-reassign */

  getRawDataObject() {
    const rawData = [];
    assign(rawData, this);
    forOwn(rawData, (value, key) => {
      rawData[key] = value.getRawDataObject();
    });
    return rawData;
  }

  /**
   *
   * @param {string} propertyId
   * @returns {LayoutProperty|Object}
   */
  getProperty(propertyId, byContentProperty = false) {
    return LayoutComponent._findProperty(
      this,
      this.getFlattenProperties(),
      propertyId,
      byContentProperty
    );
  }

  /**
   *
   * @param {string} propertyId
   * @returns {LayoutProperty|Object}
   */
  getLayoutItem(propertyId) {
    let result;
    if (this instanceof LayoutComponentLayoutItemsArray) {
      result = find(this, {name: propertyId});
    } else {
      result = this[propertyId];
    }
    return result;
  }

  getSortedPropertyNames() {
    return map(this, KEYWORD_PROP_NAME);
  }

  getFlattenProperties() {
    return this.$flattenProperties;
  }

  // eslint-disable-next-line class-methods-use-this
  toString() {
    return '[object Array]';
  }
}

export class LayoutComponentLayoutItems {
  constructor(layoutItemsDefinition) {
    assign(this, layoutItemsDefinition);

    this.extendProperties();
    this.$flattenProperties = LayoutComponent._createFlattenProperties(this);
    Object.defineProperty(this, '$flattenProperties', {enumerable: false});
  }

  extendProperties() {
    forOwn(this, (value, key) => {
      if (
        this[key][KEYWORD_PROP_LAYOUT_TYPE] &&
        this[key][KEYWORD_PROP_LAYOUT_TYPE] === KEYWORD_CONTAINER
      ) {
        this[key] = new LayoutContainer(value);
      } else {
        this[key] = new LayoutProperty(value);
      }
    });
  }

  getRawDataObject() {
    const rawData = {};
    assign(rawData, this);
    forOwn(rawData, (value, key) => {
      rawData[key] = value.getRawDataObject();
    });
    return rawData;
  }

  getFlattenProperties() {
    return this.$flattenProperties;
  }

  /**
   *
   * @param {string} propertyId
   * @returns {LayoutProperty|Object}
   */
  getProperty(propertyId, byContentProperty = false) {
    return LayoutComponent._findProperty(
      this,
      this.getFlattenProperties(),
      propertyId,
      byContentProperty
    );
  }

  /**
   *
   * @param {string} propertyId
   * @returns {LayoutProperty|Object}
   */
  getLayoutItem(propertyId) {
    let result;
    if (this instanceof LayoutComponentLayoutItemsArray) {
      result = find(this, {name: propertyId});
    } else {
      result = this[propertyId];
    }
    return result;
  }

  getSortedPropertyNames() {
    let layoutItemsKeys;
    if (isArray(this)) {
      layoutItemsKeys = map(this, KEYWORD_PROP_NAME);
    } else {
      layoutItemsKeys = Object.keys(this).sort(
        (a, b) =>
          this.getLayoutItem(a).getOrder() - this.getLayoutItem(b).getOrder()
      );
    }
    return layoutItemsKeys;
  }
}

export class LayoutSchema {
  constructor(layoutDefinition) {
    assign(this, layoutDefinition);

    this.extendComponents();
  }

  extendComponents() {
    this[KEYWORD_SCHEMA_CORE] = new LayoutComponent(this.getCore());

    forOwn(this[KEYWORD_SCHEMA_DEPENDENCIES], (value, key) => {
      this[KEYWORD_SCHEMA_DEPENDENCIES][key] = new LayoutComponent(value);
    });
  }

  /**
   * Gets core definition of screen layout.
   * @returns {LayoutSchema.core|Object} Core definition of screen layout.
   */
  getCore() {
    return this[KEYWORD_SCHEMA_CORE];
  }

  /**
   * Gets definition of dependencies of the screen layout.
   * @returns {LayoutSchema.dependencies|Object} Definition of dependencies of the screen layout.
   */
  getDependencies() {
    return this[KEYWORD_SCHEMA_DEPENDENCIES];
  }

  /**
   * Gets screen id (type of screen).
   * @returns {String} Screen id (type of screen).
   */
  getScreenId() {
    const screenId = this.getCore().id;
    const endOffset = screenId.indexOf('-layout');
    return screenId.substring(
      screenId.lastIndexOf('/') + 1,
      endOffset >= 0 ? endOffset : screenId.length
    );
  }

  /**
   * Gets screen title.
   * @returns {String} Screen title.
   */
  getScreenTitle() {
    return this.getCore().title;
  }

  /**
   * Gets definition of component layout.
   * @param componentId Identifier of component.
   * @returns {LayoutComponent|Object} Definition of component layout.
   */
  getComponent(componentId) {
    let componentLayout;
    if (this.getCore()[KEYWORD_COMP_ID] === componentId) {
      componentLayout = this.getCore();
    } else {
      // get content from dependencies
      componentLayout = this[KEYWORD_SCHEMA_DEPENDENCIES][componentId];
    }
    return componentLayout;
  }

  /**
   *
   * @returns {LayoutComponent.layoutType|string}
   */
  getLayoutType() {
    return this.getCore()[KEYWORD_COMP_LAYOUT_TYPE];
  }

  /**
   *
   * @returns {LayoutComponent.customConfig|Object}
   */
  getCustomConfig() {
    return this.getCore()[KEYWORD_COMP_CUSTOM_CONFIG];
  }

  /**
   *
   * @returns {LayoutComponent.componentType|string}
   */
  getComponentType() {
    return this.getCore()[KEYWORD_COMP_COMPONENT_TYPE];
  }
}

export default {
  LayoutSchema,
  LayoutComponent,
  LayoutComponentLayoutItems,
  LayoutComponentLayoutItemsArray,
  LayoutProperty,
};
